import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, 
  Calendar,
  Clock,
  Briefcase,
  Wrench
} from 'lucide-react';
import { formatDate, getStatusColor, getStatusStyle, getPriorityColor } from '../lib/utils';
import MapButtons from '../components/MapButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyJobs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyJobs();
  }, []);

  const fetchMyJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs/my`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch my jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            My Jobs
          </h1>
          <p className="text-slate-500 mt-1">Jobs assigned to you</p>
        </div>
        <Link to="/timesheet" className="text-amber-600 hover:text-amber-700 font-medium flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Go to Timesheet
        </Link>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No jobs assigned</h3>
            <p className="text-slate-500">You don't have any jobs assigned to you yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <Card 
              key={job.id} 
              className="border-slate-200 hover:border-amber-500/50 transition-all cursor-pointer"
              onClick={() => navigate(`/jobs/${job.id}`)}
              data-testid={`my-job-card-${job.id}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    {job.job_number && (
                      <span className="text-xs font-mono text-amber-600 bg-amber-50 px-2 py-0.5 rounded mb-2 inline-block">{job.job_number}</span>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{job.job_title}</h3>
                      <Badge className={getPriorityColor(job.priority)}>{job.priority}</Badge>
                    </div>
                    <p className="text-slate-600 mb-2">{job.client_name}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.service_address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(job.date)}
                        {job.time && ` at ${job.time}`}
                      </span>
                      {job.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.duration}h
                        </span>
                      )}
                    </div>
                    {job.assigned_equipment_ids?.length > 0 && (
                      <div className="flex items-start gap-1 text-sm text-slate-500 mt-2" data-testid={`my-job-card-equipment-${job.id}`}>
                        <Wrench className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">
                          {job.assigned_equipment_ids.map(eid => (job.assigned_equipment_snapshot || {})[eid] || eid).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={getStatusColor(job.status)} style={getStatusStyle(job.status) || undefined}>{job.status}</Badge>
                    <Badge variant="outline">{job.service_type}</Badge>
                    <MapButtons 
                      address={job.service_address} 
                      showLabels={false}
                      variant="ghost"
                    />
                  </div>
                </div>
                {job.notes && (
                  <p className="mt-3 text-sm text-slate-500 line-clamp-2 border-t border-slate-100 pt-3">
                    {job.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
